// Alle figurer tegnes én gang med Graphics og bages til teksturer. Ingen billedfiler.
// Hver tekstur har sit eget ankerpunkt (fx foden af en by eller navet på en mølle), så sprites kan
// placeres direkte på en node. Mister WebGL konteksten, tegnes de samme teksturer igen (rebake).

import { Graphics, Matrix, Rectangle, RenderTexture, Sprite } from 'pixi.js';
import type { Renderer, Texture } from 'pixi.js';
import { C, mix } from './palette';
import { SMAA_DRAWS } from './smaa-art';
import { M2_DRAWS } from './threats-m2';
import { M5_DRAWS } from './threats-m5';
import { VOLT_DRAWS } from './volt-art';

const OUT = C.ink;
const OW = 2.2;
const RES = 2;

type Draw = (g: Graphics) => void;

interface Entry {
  draw: Draw;
  tex: RenderTexture;
  frame: Rectangle;
}

// ---------------------------------------------------------------------------
// Små tegnehjælpere

function softShadow(g: Graphics, x: number, y: number, rx: number, ry: number, alpha = 0.2): void {
  for (let i = 4; i >= 1; i--) {
    g.ellipse(x, y, rx * (0.7 + i * 0.1), ry * (0.7 + i * 0.1)).fill({ color: OUT, alpha: alpha / 4 });
  }
}

function eyesOpen(g: Graphics, dx = 5.5, y = 0, s = 1): void {
  for (const sx of [-dx, dx]) {
    g.ellipse(sx, y, 2.5 * s, 3.3 * s).fill(OUT);
    g.circle(sx + 0.9 * s, y - 1.2 * s, 1 * s).fill(C.white);
  }
}

function arcEye(g: Graphics, x: number, y: number, w: number, up: boolean, width = 1.8): void {
  g.moveTo(x - w, y);
  g.quadraticCurveTo(x, y + (up ? -w * 1.1 : w * 1.1), x + w, y);
  g.stroke({ width, color: OUT, cap: 'round' });
}

/** Byens krop: hus med tag og naboer efter størrelse. Fod i (0,0). Ansigtet sidder i (0,-13). */
function cityBody(tier: number, lit: boolean): Draw {
  const wall = lit ? C.wallWarm : C.stoneDim;
  const win = lit ? C.yellow : C.windowUnlit;
  const tone = (c: number): number => (lit ? c : mix(c, C.dusk, 0.55));
  return (g) => {
    const w = 15 + tier * 1.2;
    softShadow(g, 0, 1, w + 8 + tier * 4, 5 + tier * 0.6, 0.22);
    // Høje bygninger bagved.
    if (tier >= 4) {
      g.roundRect(4, -62, 16, 62, 2).fill(tone(C.facade)).stroke({ width: OW, color: OUT });
      g.moveTo(12, -62).lineTo(12, -70).stroke({ width: 1.8, color: OUT, cap: 'round' });
      g.circle(12, -71, 1.8).fill(lit ? C.orange : C.dusk);
      for (let r = 0; r < 5; r++) for (const cx of [8, 14]) g.roundRect(cx, -56 + r * 9, 3.5, 4, 0.8).fill(win);
    }
    if (tier >= 3) {
      g.roundRect(-26, -48, 18, 48, 2).fill(tone(C.facadeLight)).stroke({ width: OW, color: OUT });
      for (let r = 0; r < 4; r++) for (const cx of [-22, -16]) g.roundRect(cx, -43 + r * 9, 3.5, 4, 0.8).fill(win);
    }
    // Naboer.
    const neighbour = (x: number, wd: number, h: number, roof: number): void => {
      g.roundRect(x, -h, wd, h, 2).fill(wall).stroke({ width: OW, color: OUT });
      g.poly([x - 2.5, -h + 1, x + wd / 2, -h - wd * 0.55, x + wd + 2.5, -h + 1], true)
        .fill(tone(roof))
        .stroke({ width: OW, color: OUT, join: 'round' });
      g.roundRect(x + wd / 2 - 2.5, -h * 0.62, 5, 5, 1).fill(win);
    };
    if (tier >= 1) neighbour(-w - 15, 15, 17, C.green);
    if (tier >= 2) neighbour(w + 1, 15, 19, C.turquoise);
    // Hovedhuset med ansigt.
    g.roundRect(-w, -27, w * 2, 27, 3).fill(wall).stroke({ width: OW, color: OUT });
    g.poly([-w - 4, -25, 0, -42 - tier, w + 4, -25], true).fill(tone(C.petrol)).stroke({ width: OW, color: OUT, join: 'round' });
    // Skorsten og vindue i gavlen.
    g.roundRect(w - 9, -40, 5, 9, 1).fill(tone(C.petrol)).stroke({ width: 1.6, color: OUT });
    g.circle(0, -31 - tier * 0.4, 2.6).fill(win).stroke({ width: 1.4, color: OUT });
    // Dør.
    g.roundRect(w - 9, -9, 6, 9, 2).fill(tone(C.contrast));
  };
}

function datacenterBody(lit: boolean): Draw {
  return (g) => {
    softShadow(g, 0, 1, 28, 6);
    g.roundRect(-22, -30, 44, 30, 3).fill(lit ? C.hall : C.stoneDim).stroke({ width: OW, color: OUT });
    g.roundRect(-22, -36, 44, 7, 2).fill(lit ? C.petrol : C.roofUnlit).stroke({ width: OW, color: OUT });
    for (let i = 0; i < 4; i++) g.roundRect(-19 + i * 10, -8, 7, 4, 1).fill(lit ? C.green : C.windowUnlit);
  };
}

function turbineTower(g: Graphics): void {
  softShadow(g, 0, 1, 11, 3.5);
  g.ellipse(0, 0, 8, 3).fill(C.grass).stroke({ width: 1.6, color: OUT });
  g.poly([-3, 0, -1.4, -44, 1.4, -44, 3, 0], true).fill(C.white).stroke({ width: OW, color: OUT, join: 'round' });
  g.roundRect(-3.5, -48, 9, 6, 2.5).fill(C.white).stroke({ width: 1.8, color: OUT });
}

function blades(g: Graphics): void {
  for (let i = 0; i < 3; i++) {
    const a = (i * Math.PI * 2) / 3 - Math.PI / 2;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const pt = (along: number, side: number): [number, number] => [cos * along - sin * side, sin * along + cos * side];
    const p = [pt(2, -2.2), pt(8, -3.4), pt(21, -1), pt(22, 0.4), pt(8, 1.6), pt(2, 1.8)].flat();
    g.poly(p, true).fill(C.white).stroke({ width: 1.7, color: OUT, join: 'round' });
  }
  g.circle(0, 0, 3.4).fill(C.white).stroke({ width: 1.8, color: OUT });
  g.circle(0, 0, 1.2).fill(C.green);
}

function solar(g: Graphics): void {
  softShadow(g, 0, 1, 22, 5);
  g.moveTo(-10, 0).lineTo(-10, -9).moveTo(10, 0).lineTo(10, -12).stroke({ width: 2, color: OUT, cap: 'round' });
  const pts = [-20, -6, 16, -10, 22, -26, -14, -22];
  g.poly(pts, true).fill(C.turquoise).stroke({ width: OW, color: OUT, join: 'round' });
  // Celler.
  for (let i = 1; i < 4; i++) {
    const t = i / 4;
    g.moveTo(-20 + 36 * t, -6 - 4 * t).lineTo(-14 + 36 * t, -22 - 4 * t);
  }
  g.moveTo(-17, -14).lineTo(19, -18);
  g.stroke({ width: 1, color: C.white, alpha: 0.75 });
  g.poly([-15, -20, -9, -21, -11, -15], true).fill({ color: C.white, alpha: 0.55 });
}

function plant(g: Graphics): void {
  softShadow(g, 0, 1, 26, 5.5);
  g.roundRect(10, -46, 9, 46, 2).fill(C.concrete).stroke({ width: OW, color: OUT });
  g.rect(10, -40, 9, 5).fill(C.yellow);
  g.poly([-22, 0, -22, -22, -13, -28, -13, -22, -4, -28, -4, -22, 5, -28, 5, -22, 12, -22, 12, 0], true)
    .fill(C.steel)
    .stroke({ width: OW, color: OUT, join: 'round' });
  for (const x of [-18, -9, 0]) g.roundRect(x, -15, 5, 6, 1).fill(C.yellow);
}

function station(g: Graphics): void {
  softShadow(g, 0, 1, 20, 5);
  g.roundRect(-18, -6, 36, 6, 2).fill(C.concrete).stroke({ width: 1.8, color: OUT });
  g.roundRect(-14, -24, 16, 18, 2).fill(C.petrol).stroke({ width: OW, color: OUT });
  for (let i = 0; i < 3; i++) g.moveTo(-12, -20 + i * 5).lineTo(0, -20 + i * 5);
  g.stroke({ width: 1.2, color: C.white, alpha: 0.5 });
  g.moveTo(8, -6).lineTo(8, -28).moveTo(14, -6).lineTo(14, -28).moveTo(5, -24).lineTo(17, -24).stroke({ width: 1.8, color: OUT, cap: 'round' });
  bolt(g, -6, -15, 0.55);
}

function bolt(g: Graphics, x: number, y: number, s: number): void {
  const p = [2, -12, -6, 1, -1, 1, -3, 12, 6, -2, 1, -2, 4, -12].map((v, i) => (i % 2 === 0 ? x + v * s : y + v * s));
  g.poly(p, true).fill(C.yellow).stroke({ width: 1.6, color: OUT, join: 'round' });
}

function offshoreBase(g: Graphics): void {
  g.ellipse(0, 0, 13, 4.5).fill({ color: C.white, alpha: 0.65 });
  g.ellipse(0, 0, 9, 3).fill(C.seaDeep);
  g.roundRect(-4, -9, 8, 9, 1.5).fill(C.yellow).stroke({ width: 1.8, color: OUT });
  g.roundRect(-8, -12, 16, 4, 1.5).fill(C.concrete).stroke({ width: 1.6, color: OUT });
  g.poly([-2.6, -12, -1.3, -50, 1.3, -50, 2.6, -12], true).fill(C.white).stroke({ width: OW, color: OUT, join: 'round' });
  g.roundRect(-3.5, -54, 9, 6, 2.5).fill(C.white).stroke({ width: 1.8, color: OUT });
}

function foreign(g: Graphics): void {
  softShadow(g, 0, 2, 16, 4);
  g.circle(0, -14, 14).fill(C.petrol).stroke({ width: OW, color: C.white });
  g.circle(0, -14, 15.6).stroke({ width: 1.2, color: OUT, alpha: 0.6 });
  // Tovejspil.
  g.moveTo(-7, -17).lineTo(7, -17).moveTo(7, -11).lineTo(-7, -11).stroke({ width: 2.2, color: C.white, cap: 'round' });
  g.poly([3, -21, 8, -17, 3, -13], true).fill(C.white);
  g.poly([-3, -15, -8, -11, -3, -7], true).fill(C.white);
}

function cloud(angry: boolean): Draw {
  return (g) => {
    const puffs: [number, number, number][] = [
      [-30, 6, 18],
      [-12, -6, 24],
      [12, -10, 26],
      [32, 4, 19],
      [0, 10, 22],
      [-22, 14, 14],
      [22, 14, 15],
    ];
    for (const [x, y, r] of puffs) g.circle(x, y + 5, r + 2.5).fill({ color: OUT, alpha: 0.12 });
    for (const [x, y, r] of puffs) g.circle(x, y, r + OW).fill(OUT);
    for (const [x, y, r] of puffs) g.circle(x, y, r).fill(angry ? C.cloud : C.cloudLight);
    g.circle(-10, -14, 11).fill({ color: C.white, alpha: 0.35 });
    g.circle(10, -18, 9).fill({ color: C.white, alpha: 0.3 });
    // Ansigt.
    eyesOpen(g, 9, 2, 1.5);
    if (angry) {
      g.moveTo(-15, -6).lineTo(-5, -3).moveTo(15, -6).lineTo(5, -3).stroke({ width: 2.4, color: OUT, cap: 'round' });
      g.moveTo(-6, 15).quadraticCurveTo(0, 10, 6, 15).stroke({ width: 2.2, color: OUT, cap: 'round' });
    } else {
      g.moveTo(-14, -6).lineTo(-6, -7).moveTo(14, -8).lineTo(5, -5).stroke({ width: 2.2, color: OUT, cap: 'round' });
      g.moveTo(-6, 12).quadraticCurveTo(1, 17, 8, 11).stroke({ width: 2.2, color: OUT, cap: 'round' });
    }
    g.ellipse(-17, 10, 4, 2.4).fill({ color: C.orange, alpha: 0.35 });
    g.ellipse(17, 10, 4, 2.4).fill({ color: C.orange, alpha: 0.35 });
  };
}

function van(g: Graphics): void {
  softShadow(g, 0, 1, 17, 3.5);
  g.roundRect(-15, -15, 22, 12, 3).fill(C.green).stroke({ width: OW, color: OUT });
  g.poly([7, -15, 11, -15, 16, -9, 16, -3, 7, -3], true).fill(C.green).stroke({ width: OW, color: OUT, join: 'round' });
  g.poly([9, -13, 11, -13, 14, -9, 9, -9], true).fill(C.white);
  g.rect(-12, -11, 16, 3).fill({ color: C.white, alpha: 0.85 });
  g.roundRect(-9, -19, 10, 4, 1.5).fill(C.yellow).stroke({ width: 1.4, color: OUT });
  for (const x of [-9, 10]) g.circle(x, -2.5, 3.4).fill(OUT).circle(x, -2.5, 1.3).fill(C.concrete);
}

function ship(g: Graphics): void {
  g.ellipse(0, 0, 20, 4).fill({ color: C.white, alpha: 0.6 });
  g.poly([-19, -8, 19, -8, 14, 0, -15, 0], true).fill(C.petrol).stroke({ width: OW, color: OUT, join: 'round' });
  g.rect(-16, -6, 32, 2).fill(C.yellow);
  g.roundRect(-8, -18, 14, 10, 2).fill(C.white).stroke({ width: 1.8, color: OUT });
  g.roundRect(-5, -15, 8, 3, 1).fill(C.turquoise);
  g.moveTo(10, -8).lineTo(10, -20).stroke({ width: 1.6, color: OUT });
  g.circle(10, -21, 1.8).fill(C.green);
}

function digger(g: Graphics): void {
  softShadow(g, 0, 1, 20, 4);
  g.roundRect(-16, -7, 26, 7, 3.5).fill(OUT);
  for (const x of [-12, -6, 0, 6]) g.circle(x, -3.5, 1.6).fill(C.metal);
  g.roundRect(-14, -19, 18, 12, 2.5).fill(C.yellow).stroke({ width: OW, color: OUT });
  g.roundRect(-11, -26, 10, 8, 2).fill(C.yellow).stroke({ width: 1.8, color: OUT });
  g.roundRect(-9, -24, 6, 4, 1).fill(C.turquoise);
  g.moveTo(3, -15).lineTo(14, -26).lineTo(21, -12).stroke({ width: 3.2, color: OUT, cap: 'round', join: 'round' });
  g.moveTo(3, -15).lineTo(14, -26).lineTo(21, -12).stroke({ width: 1.6, color: C.yellow, cap: 'round', join: 'round' });
  g.poly([17, -13, 25, -13, 23, -6, 18, -7], true).fill(C.metal).stroke({ width: 1.6, color: OUT, join: 'round' });
}

function anchorBoat(g: Graphics): void {
  g.ellipse(0, 0, 17, 3.5).fill({ color: C.white, alpha: 0.6 });
  g.poly([-15, -8, 15, -8, 11, 0, -12, 0], true).fill(C.orange).stroke({ width: OW, color: OUT, join: 'round' });
  g.roundRect(-6, -17, 10, 9, 2).fill(C.white).stroke({ width: 1.8, color: OUT });
  g.moveTo(-11, -8).lineTo(-11, -24).lineTo(-3, -17).stroke({ width: 1.6, color: OUT, cap: 'round', join: 'round' });
  // Ankeret.
  g.moveTo(13, -6).lineTo(18, 6).stroke({ width: 1.2, color: OUT });
  g.moveTo(18, 6).lineTo(18, 14).moveTo(14, 12).quadraticCurveTo(18, 17, 22, 12).stroke({ width: 1.8, color: OUT, cap: 'round' });
}

function warning(g: Graphics): void {
  softShadow(g, 0, 22, 12, 3);
  g.moveTo(0, -18).lineTo(17, 12).lineTo(-17, 12).closePath();
  g.fill(C.orange).stroke({ width: 3, color: C.white, join: 'round' });
  g.moveTo(0, -21).lineTo(20, 14).lineTo(-20, 14).closePath().stroke({ width: 1.4, color: OUT, alpha: 0.7, join: 'round' });
  g.roundRect(-1.8, -8, 3.6, 11, 1.8).fill(OUT);
  g.circle(0, 7.5, 2).fill(OUT);
}

function tree(variant: number): Draw {
  return (g) => {
    g.ellipse(0, 0, 6, 2).fill({ color: OUT, alpha: 0.12 });
    g.rect(-1, -5, 2, 5).fill(C.wood);
    if (variant === 0) {
      g.circle(0, -10, 6).fill(C.tree);
      g.circle(-2, -12, 2.4).fill({ color: C.white, alpha: 0.25 });
    } else {
      g.poly([0, -19, 6, -4, -6, -4], true).fill(C.treeDark);
      g.poly([0, -19, 2, -14, -1, -12], true).fill({ color: C.white, alpha: 0.2 });
    }
  };
}

function pylon(g: Graphics): void {
  g.moveTo(-4, 0).lineTo(0, -16).lineTo(4, 0).moveTo(-3, -6).lineTo(3, -6).moveTo(-6, -13).lineTo(6, -13).moveTo(-2.5, -6).lineTo(1.8, -11).moveTo(2.5, -6).lineTo(-1.8, -11);
  g.stroke({ width: 1.3, color: mix(OUT, C.white, 0.25), cap: 'round', join: 'round' });
}

function zzz(g: Graphics): void {
  const z = (x: number, y: number, s: number): void => {
    g.moveTo(x - s, y - s).lineTo(x + s, y - s).lineTo(x - s, y + s).lineTo(x + s, y + s);
  };
  z(0, 0, 3);
  z(7, -7, 2.2);
  z(12, -13, 1.6);
  g.stroke({ width: 1.8, color: C.petrol, cap: 'round', join: 'round' });
}

/** Alle navngivne teksturer. Navnet er nøglen, funktionen tegner omkring ankerpunktet. */
const DRAWS: Record<string, Draw> = {
  dot: (g) => {
    // Et varmt skær bagt ind, så prikkerne lyser på det lyse kort uden et filter.
    for (let i = 4; i >= 1; i--) g.circle(0, 0, 5 + i * 1.6).fill({ color: C.glowWarm, alpha: 0.11 });
    g.circle(0, 0, 5).fill(C.white);
    g.circle(0, 0, 3.4).fill(C.yellow);
    g.circle(-1, -1.1, 1.2).fill({ color: C.white, alpha: 0.85 });
  },
  glow: (g) => {
    for (let i = 10; i >= 1; i--) g.circle(0, 0, i * 3).fill({ color: C.white, alpha: 0.06 });
  },
  puff: (g) => {
    g.circle(0, 0, 10).fill({ color: C.white, alpha: 0.5 });
    g.circle(-1, -1, 7).fill({ color: C.white, alpha: 0.7 });
    g.circle(-2, -2, 4).fill({ color: C.white, alpha: 0.9 });
  },
  ring: (g) => {
    g.circle(0, 0, 30).stroke({ width: 4, color: C.white });
  },
  spark: (g) => {
    g.poly([0, -7, 1.6, -1.6, 7, 0, 1.6, 1.6, 0, 7, -1.6, 1.6, -7, 0, -1.6, -1.6], true).fill(C.white);
  },
  rain: (g) => {
    g.moveTo(0, 0).lineTo(-2.5, 8).stroke({ width: 1.8, color: C.turquoise, cap: 'round' });
  },
  pip: (g) => {
    g.circle(0, 0, 3.2).fill(C.white).stroke({ width: 1.6, color: OUT });
  },
  bolt: (g) => bolt(g, 0, 0, 1),
  shield: (g) => {
    g.moveTo(0, -8).lineTo(7, -5).lineTo(6, 3).quadraticCurveTo(3, 7, 0, 9).quadraticCurveTo(-3, 7, -6, 3).lineTo(-7, -5).closePath();
    g.fill(C.green).stroke({ width: 1.8, color: C.white, join: 'round' });
    g.moveTo(-3, 0).lineTo(-0.5, 3).lineTo(3.5, -3).stroke({ width: 1.8, color: C.white, cap: 'round', join: 'round' });
  },
  battery: (g) => {
    g.roundRect(-8, -5, 15, 10, 2).fill(C.white).stroke({ width: 1.8, color: OUT });
    g.rect(7, -2, 2, 4).fill(OUT);
    g.roundRect(-6, -3, 7, 6, 1).fill(C.green);
  },
  // Svinghjul: et hvidt hjul med eger og et turkis nav.
  flywheel: (g) => {
    g.circle(0, 0, 7).fill(C.white).stroke({ width: 1.8, color: OUT });
    g.moveTo(0, -6).lineTo(0, 6).moveTo(-6, 0).lineTo(6, 0).stroke({ width: 1.2, color: C.petrol, alpha: 0.7 });
    g.circle(0, 0, 2.4).fill(C.turquoise).stroke({ width: 1.2, color: OUT });
  },
  wave: (g) => {
    g.moveTo(-6, 0).quadraticCurveTo(-3, -3, 0, 0).quadraticCurveTo(3, 3, 6, 0).stroke({ width: 1.6, color: C.white, alpha: 0.9, cap: 'round' });
  },
  tree0: tree(0),
  tree1: tree(1),
  pylon,
  zzz,
  turbine: turbineTower,
  blades,
  solar,
  plant,
  station,
  offshore: offshoreBase,
  foreign,
  cloudWarn: cloud(false),
  cloudAngry: cloud(true),
  van,
  ship,
  digger,
  anchor: anchorBoat,
  warning,
  ...M2_DRAWS,
  ...M5_DRAWS,
  ...SMAA_DRAWS,
  ...VOLT_DRAWS,
  dc_lit: datacenterBody(true),
  dc_dark: datacenterBody(false),
  // Ansigter: centreret i ansigtets midte.
  eyes_open: (g) => eyesOpen(g),
  eyes_blink: (g) => {
    arcEye(g, -5.5, 0.5, 2.6, true);
    arcEye(g, 5.5, 0.5, 2.6, true);
  },
  eyes_sleep: (g) => {
    arcEye(g, -5.5, -0.5, 2.6, false);
    arcEye(g, 5.5, -0.5, 2.6, false);
  },
  eyes_worried: (g) => {
    eyesOpen(g);
    g.moveTo(-8, -6).lineTo(-3.5, -7.5).moveTo(8, -6).lineTo(3.5, -7.5).stroke({ width: 1.6, color: OUT, cap: 'round' });
  },
  eyes_angry: (g) => {
    eyesOpen(g);
    g.moveTo(-8.5, -7.5).lineTo(-3, -4.5).moveTo(8.5, -7.5).lineTo(3, -4.5).stroke({ width: 2, color: OUT, cap: 'round' });
  },
  mouth_happy: (g) => {
    g.moveTo(-4.5, 0).quadraticCurveTo(0, 7, 4.5, 0).closePath().fill(OUT);
    g.ellipse(0, 3.2, 2, 1.2).fill(C.orange);
  },
  mouth_worried: (g) => {
    g.moveTo(-4, 1.5).quadraticCurveTo(-2, -0.5, 0, 1.5).quadraticCurveTo(2, 3.5, 4, 1.5).stroke({ width: 1.6, color: OUT, cap: 'round' });
  },
  mouth_angry: (g) => {
    g.moveTo(-4.5, 3).quadraticCurveTo(0, -2, 4.5, 3).stroke({ width: 1.9, color: OUT, cap: 'round' });
  },
  mouth_sleep: (g) => {
    g.ellipse(0, 1.5, 1.6, 2).fill(OUT);
  },
  cheeks: (g) => {
    g.ellipse(-10, 0, 3.2, 2).fill({ color: C.orange, alpha: 0.45 });
    g.ellipse(10, 0, 3.2, 2).fill({ color: C.orange, alpha: 0.45 });
  },
  // Blackout: et ked ansigt, en tåre og stearinlys i vinduerne.
  eyes_sad: (g) => {
    eyesOpen(g);
    g.moveTo(-8.5, -5).lineTo(-3.5, -7.8).moveTo(8.5, -5).lineTo(3.5, -7.8).stroke({ width: 1.6, color: OUT, cap: 'round' });
  },
  mouth_sad: (g) => {
    g.moveTo(-3.6, 3.2).quadraticCurveTo(0, -0.6, 3.6, 3.2).stroke({ width: 1.7, color: OUT, cap: 'round' });
  },
  tear: (g) => {
    g.moveTo(0, -3).quadraticCurveTo(2.5, 0.8, 0, 2.6).quadraticCurveTo(-2.5, 0.8, 0, -3).closePath().fill(C.drop).stroke({ width: 1, color: OUT });
  },
  candle: (g) => {
    g.circle(0, 0, 5.5).fill({ color: C.orange, alpha: 0.28 });
    g.circle(0, 0, 3.4).fill({ color: C.yellow, alpha: 0.45 });
    g.moveTo(0, -3.4).quadraticCurveTo(2.2, -0.2, 0, 1.8).quadraticCurveTo(-2.2, -0.2, 0, -3.4).closePath().fill(C.yellow);
    g.circle(0, 0.5, 0.9).fill(C.white);
  },
};
for (let t = 0; t <= 4; t++) {
  DRAWS[`city${t}_lit`] = cityBody(t, true);
  DRAWS[`city${t}_dark`] = cityBody(t, false);
}

export class Atlas {
  private entries = new Map<string, Entry>();

  constructor(private renderer: Renderer) {
    for (const [name, draw] of Object.entries(DRAWS)) this.entries.set(name, this.bake(draw));
  }

  private bake(draw: Draw): Entry {
    const g = new Graphics();
    draw(g);
    const b = g.getLocalBounds();
    const pad = 2;
    const frame = new Rectangle(Math.floor(b.x - pad), Math.floor(b.y - pad), Math.ceil(b.width + pad * 2), Math.ceil(b.height + pad * 2));
    const tex = RenderTexture.create({
      width: frame.width,
      height: frame.height,
      resolution: RES,
      antialias: true,
      textureOptions: { defaultAnchor: { x: -frame.x / frame.width, y: -frame.y / frame.height } },
    });
    this.paint(g, tex, frame);
    g.destroy();
    return { draw, tex, frame };
  }

  private paint(g: Graphics, tex: RenderTexture, frame: Rectangle): void {
    this.renderer.render({ container: g, target: tex, clear: true, clearColor: [0, 0, 0, 0], transform: new Matrix().translate(-frame.x, -frame.y) });
  }

  /** Tegn alle teksturer igen efter tabt WebGL-kontekst. */
  rebake(): void {
    for (const e of this.entries.values()) {
      const g = new Graphics();
      e.draw(g);
      this.paint(g, e.tex, e.frame);
      g.destroy();
    }
  }

  tex(name: string): Texture {
    const e = this.entries.get(name);
    if (!e) throw new Error(`Ukendt tekstur: ${name}`);
    return e.tex;
  }

  sprite(name: string): Sprite {
    return new Sprite(this.tex(name));
  }

  /** Teksturen som billede (data-URL) til DOM'en, fx Volts portræt i panelet. */
  image(name: string): string {
    try {
      const c = this.renderer.extract.canvas(this.tex(name)) as HTMLCanvasElement;
      return c.toDataURL('image/png');
    } catch {
      return '';
    }
  }
}
